/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { Form, Input, Select, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import { FormInstance } from "antd/lib";
import ValidPassportsTable from "../../../../components/common/Table/ValidPassportsTable";
import dayjs from "dayjs";

interface PassportInfoProps {
  form: FormInstance<IVisaRequestFormData>;
  passportData: any;
}

const PassportInfo: React.FC<PassportInfoProps> = ({ form, passportData }) => {
  const { t } = useTranslation();

  // Valid passports data
  const [showValidPassports, setShowValidPassports] = useState<boolean>(
    form.getFieldValue("other_valid_passports")?.length > 0
  );
  const [validPassports, setValidPassports] = useState<IValidPassportInfo[]>(
    form.getFieldValue("other_valid_passports")?.length > 0
      ? form.getFieldValue("other_valid_passports")
      : [
          {
            key: "1",
            passport_type: "",
            passport_specify: "",
            passport_number: "",
            passport_place_of_issue: "",
            passport_date_of_issue: "",
            passport_expiry_date: ""
          }
        ]
  );
  const handleAddValidPassports = () => {
    const newData: IValidPassportInfo = {
      key: Date.now().toString(),
      passport_type: "",
      passport_specify: "",
      passport_number: "",
      passport_place_of_issue: "",
      passport_date_of_issue: "",
      passport_expiry_date: ""
    };

    setValidPassports([...validPassports, newData]);
    form.setFieldsValue({ other_passports: [...validPassports, newData] });
  };
  const handleDeleteValidPassports = (key: string) => {
    const newData = validPassports.filter((item) => item.key !== key);

    setValidPassports(newData);
    form.setFieldsValue({ other_passports: newData });
  };
  const handleChangeValidPassports = (
    key: string,
    field: keyof IValidPassportInfo,
    value: string
  ) => {
    const newData = validPassports.map((item) => {
      if (item.key === key) {
        return { ...item, [field]: value };
      }
      return item;
    });

    setValidPassports(newData);
    form.setFieldsValue({ other_passports: newData });
  };
  useEffect(() => {
    const formatDateOfIssue = passportData?.issue_date
      ? dayjs(passportData?.issue_date, "DD/MM/YYYY").format("YYYY-MM-DD")
      : undefined;
    const formatExpiryDate = dayjs(passportData?.due_date, "DD/MM/YYYY").format(
      "YYYY-MM-DD"
    );

    if (passportData) {
      form.setFieldsValue({
        request_passport_number: passportData?.id,
        passport_place_of_issue: passportData?.place_of_issue,
        passport_type: passportData?.type,
        date_of_issue: formatDateOfIssue
          ? (dayjs(formatDateOfIssue) as any)
          : undefined,
        passport_expiry_date: dayjs(formatExpiryDate) as any
      });
    }
  }, [passportData, form]);
  return (
    <>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <Form.Item
          initialValue={passportData?.id}
          layout={"vertical"}
          label={
            <label className="text-primary-blue">
              {t("registration_form.step3.passport_number")}
            </label>
          }
          name="request_passport_number"
          style={{ color: "red" }}
          rules={[
            {
              required: true,
              message: t("registration_form.empty_passport_number", {
                ns: "message"
              })
            },
            {
              pattern: /^[A-Z0-9]*$/g,
              message: t("registration_form.invalid_passport_number", {
                ns: "message"
              })
            }
          ]}
        >
          <Input
            placeholder={t("registration_form.step3.passport_number_hint")}
          />
        </Form.Item>

        {/* <Form.Item
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <label className="text-primary-blue">
              {t("registration_form.step3.place_of_issue")}
            </label>
          }
          name="passport_place_of_issue"
        >
          <Input
            placeholder={t("registration_form.step3.place_of_issue_hint")}
          />
        </Form.Item> */}

        <Form.Item
          layout={"vertical"}
          label={
            <label className="text-primary-blue">
              {t("registration_form.step3.visa_type")}
            </label>
          }
          name="passport_type"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_passport_type", {
                ns: "message"
              })
            }
          ]}
        >
          <Select placeholder={t("registration_form.step3.visa_type_hint")}>
            <Select.Option value="Ordinary">
              {t("registration_form.step3.ordinary_passport")}
            </Select.Option>
            <Select.Option value="Diplomatic">
              {t("registration_form.step3.diplomatic_passport")}
            </Select.Option>
            <Select.Option value="Official">
              {t("registration_form.step3.official_passport")}
            </Select.Option>
            <Select.Option value="Other">
              {t("registration_form.step3.other_passport")}
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          initialValue={
            passportData?.issue_date
              ? dayjs(passportData?.issue_date, "DD/MM/YYYY")
              : null
          }
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <label className="text-primary-blue">
              {t("registration_form.step3.date_of_issue")} (DD/MM/YYYY)
            </label>
          }
          name="date_of_issue"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_date_of_issue", {
                ns: "message"
              })
            },
            {
              validator: (_, value) => {
                if (value && value > dayjs()) {
                  return Promise.reject(
                    t("registration_form.invalid_date_of_issue", {
                      ns: "message"
                    })
                  );
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            className="w-full"
            placeholder={t("registration_form.step3.date_of_issue_hint")}
          />
        </Form.Item>

        <Form.Item
          initialValue={
            passportData?.due_date
              ? dayjs(passportData?.due_date, "DD/MM/YYYY")
              : null
          }
          layout={"vertical"}
          className="text-primary-blue"
          label={
            <label className="text-primary-blue">
              {t("registration_form.step3.expiry_date")} (DD/MM/YYYY)
            </label>
          }
          name="passport_expiry_date"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_expiry_date", {
                ns: "message"
              })
            },
            {
              validator: (_, value) => {
                if (value && value <= form.getFieldValue("date_of_issue")) {
                  return Promise.reject(
                    t("registration_form.invalid_expiry_date", {
                      ns: "message"
                    })
                  );
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            className="w-full"
            placeholder={t("registration_form.step3.expiry_date_hint")}
          />
        </Form.Item>
      </div>

      {/* <div className="flex max-md:flex-col max-md:gap-4 justify-between">
        <div className="text-primary-blue">
          {t("registration_form.step3.other_valid_passport")}
        </div>

        <Radio.Group
          className="flex max-md:justify-evenly"
          value={showValidPassports}
          onChange={(e) => {
            setShowValidPassports(e.target.value);
            if (!e.target.value) {
              form.setFieldsValue({ other_passports: [] });
              setValidPassports([
                {
                  key: "1",
                  passport_type: "",
                  passport_specify: "",
                  passport_number: "",
                  passport_place_of_issue: "",
                  passport_date_of_issue: "",
                  passport_expiry_date: ""
                }
              ]);
            }
          }}
        >
          <Radio value={true}>{t("registration_form.yes_option")}</Radio>
          <Radio value={false}>{t("registration_form.no_option")}</Radio>
        </Radio.Group>
      </div> */}

      {showValidPassports && (
        <ValidPassportsTable
          dataSource={validPassports}
          handleChange={handleChangeValidPassports}
          handleAdd={handleAddValidPassports}
          handleDelete={handleDeleteValidPassports}
        />
      )}
    </>
  );
};

export default PassportInfo;
